/**
 * Field parsing/defaulting/shaping for Form.tsx, kept in a plain .ts file (no JSX) so it runs
 * under `node --test` directly, matching tableData.ts's own rationale.
 *
 * Unlike Table/Checklist's `source`/`ids`/`columns`, a form's `fields` ARE the data, not a
 * reference to it — see the comment on Form.tsx's component export for why that is the
 * deliberate exception to this phase's "component arguments are references" rule.
 */

export const FIELD_TYPES = ["text", "textarea", "number", "select", "checkbox"] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

export interface FieldSpec {
	name: string;
	label: string;
	type: FieldType;
	options?: string[];
	value?: unknown;
	/** A line under the control: what the field wants, or that it may be left blank. */
	hint?: string;
}

export type ParseFieldsResult = { ok: true; fields: FieldSpec[] } | { ok: false; error: string };

/**
 * Validates `args.fields` into a usable field list, or explains exactly what is wrong with it.
 * This is rendered straight into the component's own error state (user-facing), not logged to a
 * console, per the "failures must be visible" rule — a form the model built wrong should say so,
 * not silently drop the broken field and collect an incomplete answer.
 */
export function parseFields(raw: unknown): ParseFieldsResult {
	if (!Array.isArray(raw) || raw.length === 0) {
		return { ok: false, error: "form 组件缺少 fields 参数（需要非空数组）" };
	}
	const fields: FieldSpec[] = [];
	const seen = new Set<string>();
	for (const [i, entry] of raw.entries()) {
		if (!isPlainObject(entry)) return { ok: false, error: `fields[${i}] 不是对象` };
		const { name, label, type, options, value, hint } = entry;
		if (typeof name !== "string" || name.length === 0) return { ok: false, error: `fields[${i}] 缺少 name` };
		if (seen.has(name)) return { ok: false, error: `字段名「${name}」重复` };
		if (typeof label !== "string" || label.length === 0) return { ok: false, error: `字段「${name}」缺少 label` };
		if (typeof type !== "string" || !isFieldType(type)) {
			return { ok: false, error: `字段「${name}」的 type 不支持：${String(type)}（支持 ${FIELD_TYPES.join("/")}）` };
		}
		const opts = Array.isArray(options) ? options.map(String) : undefined;
		if (type === "select" && (!opts || opts.length === 0)) {
			return { ok: false, error: `字段「${name}」为 select 类型，缺少 options` };
		}
		seen.add(name);
		fields.push({ name, label, type, options: opts, value, hint: typeof hint === "string" && hint ? hint : undefined });
	}
	return { ok: true, fields };
}

function isFieldType(value: string): value is FieldType {
	return (FIELD_TYPES as readonly string[]).includes(value);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** A field's edited value as held in the controlled inputs: every field type reduces to a string except checkbox. */
export type FieldValue = string | boolean;

/**
 * Which of `fields` could not actually be read back from a parsed answer object — either the
 * field's own key is entirely absent (not merely `null`/falsy, which is a legitimate blank answer
 * `coerceFieldValue` already renders correctly), or the key is present but its value isn't usable
 * for that field's declared type:
 *   - `select` — a value that isn't a string among that field's own `options`.
 *   - `checkbox` — a value that isn't actually a boolean.
 * Both are reachable through the same root cause a missing key is: `componentSchema`
 * (src/profile/components.ts) never ties a `render: form` component's `result` JSON schema to its
 * `fields` — nothing under `src/` references `options`/`checkbox` when validating a submission —
 * so a value the server's generic schema check accepts can still not fit what a specific field
 * declares. This matters more for `select`/`checkbox` than for `text`/`textarea`/`number`:
 * `coerceFieldValue` falls back to `options[0]` or `Boolean(raw)` for the former two, both of which
 * produce a *specific, plausible-looking, wrong* answer — not a visible blank, which is what a
 * stringified value (text/textarea) or a browser's own empty number input (an unparseable string)
 * read as for the latter three. Form.tsx surfaces every case here through the same one banner,
 * rather than letting any of these defaults stand in silently for a real submitted answer.
 */
export function unreadableAnsweredFields(fields: FieldSpec[], value: Record<string, unknown>): FieldSpec[] {
	return fields.filter((f) => {
		if (!(f.name in value)) return true;
		const raw = value[f.name];
		if (f.type === "checkbox") return typeof raw !== "boolean";
		if (f.type === "select") return !(typeof raw === "string" && f.options?.includes(raw));
		return false;
	});
}

/**
 * Coerces any raw value (a field's declared initial `value`, or one field of a parsed answer) to
 * this field's control shape: a checkbox needs a real boolean, a select needs one of its own
 * `options` (falling back to the first option rather than an empty, invalid selection), anything
 * else is a plain string.
 */
export function coerceFieldValue(field: FieldSpec, raw: unknown): FieldValue {
	if (field.type === "checkbox") return typeof raw === "boolean" ? raw : Boolean(raw);
	if (field.type === "select") {
		if (typeof raw === "string" && field.options?.includes(raw)) return raw;
		return field.options?.[0] ?? "";
	}
	if (raw === undefined || raw === null) return "";
	return typeof raw === "string" ? raw : String(raw);
}

/** A field's starting value: its declared `value` when that fits the field's type, else a sensible empty default. */
export function defaultFieldValue(field: FieldSpec): FieldValue {
	return coerceFieldValue(field, field.value);
}

/**
 * Coerces the controlled-input state (always string|boolean) back to the typed value each
 * field's result should carry: a number field submits a number, a checkbox a boolean, everything
 * else a string. A blank or non-numeric number field submits `null` rather than a silently-wrong
 * `0`, so the value reads as visibly absent instead of plausibly right.
 *
 * Whether that `null` is then REJECTED depends on the component's declared result schema. A
 * schema that constrains the field produces a 400 telling the user to go fix it. But a component
 * whose fields are assembled per call can only declare a bare `{type: object}` (jobhunt's
 * `configure` and `mark_applied` do), which accepts any object at all — so there the `null`
 * reaches the model as the user's answer. Deriving the result schema from the call's own
 * `fields` is the open follow-up against buildInteractiveExecute in src/runtime/components.ts.
 */
export function shapeFormResult(fields: FieldSpec[], state: Record<string, FieldValue>): Record<string, unknown> {
	const out: Record<string, unknown> = {};
	for (const f of fields) {
		// `state` is an OVERLAY of what the user actually edited, not the whole answer, so a field
		// they never touched falls back to its declared default -- exactly what Form.tsx renders
		// into the control. Reading bare `state` here instead is what let a form DISPLAY a
		// prefilled value and SUBMIT an empty string: the two disagreed whenever a key was
		// missing, which a remount mid-edit or a mount before the arguments finished streaming
		// both produce. `??` and not `||`, so a deliberately cleared text field still submits ""
		// and an unchecked box still submits false.
		const raw = state[f.name] ?? defaultFieldValue(f);
		if (f.type === "checkbox") {
			out[f.name] = Boolean(raw);
		} else if (f.type === "number") {
			const s = typeof raw === "string" ? raw.trim() : String(raw ?? "");
			out[f.name] = s === "" ? null : numberOrNull(s);
		} else {
			out[f.name] = typeof raw === "string" ? raw : String(raw ?? "");
		}
	}
	return out;
}

function numberOrNull(s: string): number | null {
	const n = Number(s);
	return Number.isFinite(n) ? n : null;
}
